import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import {
  paginateAppointments,
  AppointmentPaginationPayload,
  deleteAppointment,
} from "../../api/appointment";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";

const defaultPayload: AppointmentPaginationPayload = {
  curPage: 1,
  perPage: 10,
  sortBy: "appointmentDate",
  direction: "desc",
  whereClause: [],
};

const AppointmentList: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");
  const [payload, setPayload] =
    useState<AppointmentPaginationPayload>(defaultPayload);
  const [total, setTotal] = useState(0);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});
  const lastFetchKeyRef = useRef<string>("");
  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

  const formatDateForAPI = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const filters: { key: string; value: string; operator: string }[] = [];

      // Only use selectedDate if it's valid (no error)
      if (selectedDate && !dateError) {
        filters.push({
          key: "appointmentDate",
          value: selectedDate,
          operator: "=",
        });
      }

      if (searchTerm.trim()) {
        filters.push({
          key: "patientName",
          value: searchTerm.trim(),
          operator: "=",
        });
      }

      const body: AppointmentPaginationPayload = {
        ...payload,
        whereClause: filters,
      };
      const res = await paginateAppointments(body);
      const data = res?.data?.data || res?.data || {};
      setAppointments(
        Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : []
      );
      setTotal(data.total || res.data.total || 0);
    } catch (err) {
      setAppointments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if date is valid (no error) or if date is empty
    if (dateError && selectedDate) {
      return; // Don't fetch if there's a date error
    }
    
    const key = JSON.stringify({
      p: payload.curPage,
      pp: payload.perPage,
      s: payload.sortBy,
      d: payload.direction,
      date: selectedDate,
      search: searchTerm,
    });
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payload.curPage,
    payload.perPage,
    payload.sortBy,
    payload.direction,
    selectedDate,
    searchTerm,
    dateError,
  ]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the open menu and button
      const isClickOnButton = Object.values(buttonRefs.current).some(
        (btn) => btn && btn.contains(target)
      );
      const isClickOnMenu = target.closest('.dropdown-menu-container');
      
      if (!isClickOnButton && !isClickOnMenu && openMenuId) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // If empty, clear the date and error
    if (!value) {
      setSelectedDate("");
      setDateError("");
      setPayload((prev) => ({ ...prev, curPage: 1 }));
      return;
    }

    // Validate date format (YYYY-MM-DD) - must be exactly this format
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = value.match(dateRegex);
    
    // If format doesn't match, don't allow the input - keep previous value
    if (!match) {
      e.target.value = selectedDate; // Revert to previous valid value
      return;
    }

    // Extract year, month, day from the input string directly
    const yearStr = match[1];
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);

    // Prevent year from having more than 4 digits - truncate if needed
    if (yearStr.length > 4) {
      const truncatedYear = yearStr.substring(0, 4);
      const correctedValue = `${truncatedYear}-${match[2]}-${match[3]}`;
      setSelectedDate(correctedValue);
      e.target.value = correctedValue;
      setDateError("");
      setPayload((prev) => ({ ...prev, curPage: 1 }));
      return;
    }

    const year = parseInt(yearStr, 10);

    // Validate month first (1-12)
    if (isNaN(month) || month < 1 || month > 12) {
      e.target.value = selectedDate; // Revert to previous valid value
      return;
    }

    // Validate day (1-31)
    if (isNaN(day) || day < 1 || day > 31) {
      e.target.value = selectedDate; // Revert to previous valid value
      return;
    }

    // Validate year - check if it's a valid number (not NaN, not Infinity)
    if (isNaN(year) || !isFinite(year)) {
      e.target.value = selectedDate; // Revert to previous valid value
      return;
    }

    // Create date object to validate the actual date (handles leap years, invalid dates)
    const date = new Date(year, month - 1, day);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      e.target.value = selectedDate; // Revert to previous valid value
      return;
    }

    // Verify the parsed date matches the input (handles invalid dates like Feb 30)
    const parsedMonth = date.getMonth() + 1;
    const parsedDay = date.getDate();

    // Check if month and day match (handles invalid dates like Feb 30)
    if (parsedMonth !== month || parsedDay !== day) {
      e.target.value = selectedDate; // Revert to previous valid value
      return;
    }

    // Date is valid, set it and clear any errors
    setSelectedDate(value);
    setDateError("");
    setPayload((prev) => ({ ...prev, curPage: 1 }));
  };

  const clearDateFilter = () => {
    setSelectedDate("");
    setDateError("");
    setPayload((prev) => ({ ...prev, curPage: 1 }));
  };

  const handleDateInput = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    
    // If empty, allow it
    if (!value) {
      return;
    }

    // For date inputs, check if the year part exceeds 4 digits
    // Date format is YYYY-MM-DD
    const parts = value.split('-');
    if (parts.length >= 1 && parts[0]) {
      const yearStr = parts[0];
      // If year has more than 4 digits, truncate it to 4
      if (yearStr.length > 4) {
        const truncatedYear = yearStr.substring(0, 4);
        const correctedValue = parts.length >= 3
          ? `${truncatedYear}-${parts[1]}-${parts[2]}`
          : parts.length === 2
          ? `${truncatedYear}-${parts[1]}`
          : truncatedYear;
        
        // Use setTimeout to ensure the value is set after browser processing
        setTimeout(() => {
          e.currentTarget.value = correctedValue;
          setSelectedDate(correctedValue);
        }, 0);
      }
    }
  };

  const handleDateBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Re-validate on blur to catch any invalid values that might have been entered
    const value = e.target.value;
    if (value && dateError) {
      // If there's an error, clear the invalid date
      setSelectedDate("");
      e.target.value = "";
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPayload((prev) => ({ ...prev, curPage: 1 }));
  };

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };

  const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "SCHEDULED":
        return "bg-blue-100 text-blue-800";
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "CANCELLED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return "—";
    
    // Extract hours and minutes from HH:mm or HH:mm:ss format
    let hours = 0;
    let minutes = 0;
    
    if (timeString.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
      const parts = timeString.split(":");
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
    } else {
      return timeString;
    }
    
    // Convert to 12-hour format
    const period = hours >= 12 ? "PM" : "AM";
    let displayHours = hours % 12;
    if (displayHours === 0) displayHours = 12; // 0 or 12 should show as 12
    
    // Format minutes with leading zero if needed
    const displayMinutes = String(minutes).padStart(2, "0");
    
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  const formatAppointmentDate = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const askDelete = (id?: string) => {
    const appointmentId = id || "";
    if (!appointmentId) return;
    setSelectedAppointmentId(appointmentId);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAppointmentId) return;
    setDeleting(true);
    try {
      await deleteAppointment(selectedAppointmentId);
      toast.success("Appointment deleted successfully");
      setConfirmOpen(false);
      setSelectedAppointmentId("");
      fetchAppointments();
    } catch (err) {
      const e: any = err;
      const msg = e?.response?.data?.message || "Failed to delete appointment";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              {/* Header Row */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Appointments
                </h2>
                <button
                  className="px-5 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                  onClick={() => navigate("/appointments/create")}
                >
                  + New Appointment
                </button>
              </div>

              {/* Filters & Summary */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                {/* Date Filter & Search */}
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700">
                      Filter by Date:
                    </label>
                    <div className="flex flex-col">
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        onInput={handleDateInput}
                        onBlur={handleDateBlur}
                        max="9999-12-31"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    {selectedDate && !dateError && (
                      <button
                        type="button"
                        onClick={clearDateFilter}
                        className="px-3 py-1 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Clear Date
                      </button>
                    )}
                  </div>
                 
                </div>

                {/* Summary & rows per page */}
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex items-center gap-2">
                     <div className="w-full md:w-64">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search by patient name..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                    <span className="text-sm text-gray-600">Rows:</span>
                    <select
                      className="border rounded-lg p-1 select-plain"
                      value={payload.perPage}
                      onChange={changePerPage}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Patient Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Date
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Time
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Clinic Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={6}
                        >
                          {selectedDate
                            ? `No appointments found for ${formatDateForAPI(
                                selectedDate
                              )}`
                            : "No appointments found"}
                        </td>
                      </tr>
                    ) : (
                      appointments.map((appointment: any) => (
                        <tr
                          key={appointment.id || appointment._id}
                          className="border-t hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {appointment.patient_name ||
                              "Unknown Patient"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatAppointmentDate(
                              appointment.appointmentDate || appointment.date
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatTime(appointment.appointmentTime)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {appointment.clinic_name
                              ||
                              "—"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                appointment.status
                              )}`}
                            >
                              {appointment.status || "Unknown"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div 
                              className="relative" 
                              ref={(el) => {
                                const appointmentId = appointment.id || appointment._id || "";
                                if (openMenuId === appointmentId) {
                                  (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                }
                              }}
                            >
                              <button
                                ref={(el) => {
                                  if (el) {
                                    const appointmentId = appointment.id || appointment._id || "";
                                    buttonRefs.current[appointmentId] = el;
                                  }
                                }}
                                className="text-gray-600 hover:text-gray-900 focus:outline-none p-1 rounded-full hover:bg-gray-100"
                                onClick={(e) => {
                                  const appointmentId = appointment.id || appointment._id || "";
                                  if (openMenuId === appointmentId) {
                                    setOpenMenuId(null);
                                    setDropdownPosition(null);
                                  } else {
                                    const button = e.currentTarget;
                                    const rect = button.getBoundingClientRect();
                                    setDropdownPosition({
                                      top: rect.bottom + window.scrollY + 4,
                                      left: rect.right + 4,
                                    });
                                    setOpenMenuId(appointmentId);
                                  }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="5" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="12" cy="19" r="2" />
                                </svg>
                              </button>
                              {openMenuId === (appointment.id || appointment._id) && dropdownPosition && createPortal(
                                <div 
                                  className="dropdown-menu-container fixed w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    left: `${dropdownPosition.left}px`,
                                  }}
                                >
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      const appointmentId = appointment.id || appointment._id;
                                      navigate(`/appointments/create/${appointmentId}`);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      askDelete(appointment.id || appointment._id);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>,
                                document.body
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination - only show when there are appointments */}
              {appointments.length > 0 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-600">
                    Page {payload.curPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                      onClick={() => goToPage(1)}
                      disabled={payload.curPage === 1}
                    >
                      « First
                    </button>
                    <button
                      className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                      onClick={() => goToPage(payload.curPage - 1)}
                      disabled={payload.curPage === 1}
                    >
                      ‹ Prev
                    </button>
                    <button
                      className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                      onClick={() => goToPage(payload.curPage + 1)}
                      disabled={payload.curPage >= totalPages}
                    >
                      Next ›
                    </button>
                    <button
                      className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                      onClick={() => goToPage(totalPages)}
                      disabled={payload.curPage >= totalPages}
                    >
                      Last »
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
            <ConfirmModal
              isOpen={confirmOpen}
              title="Delete Appointment"
              message="Are you sure you want to delete this appointment? This action cannot be undone."
              confirmText="Delete"
              cancelText="Cancel"
              onConfirm={handleConfirmDelete}
              onCancel={() => setConfirmOpen(false)}
              loading={deleting}
              variant="danger"
            />
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default AppointmentList;
